import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../../lib/supabase";

const REDIRECT_URL = `${window.location.origin}/auth/callback`;

export default function Login() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [googleStatus, setGoogleStatus] = useState<"idle" | "redirecting" | "error">("idle");
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus("sending");
    setError(null);
    const { error: err } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: REDIRECT_URL, shouldCreateUser: false },
    });
    if (err) {
      setStatus("error");
      setError(err.message);
      return;
    }
    setStatus("sent");
  };

  const handleGoogleSignIn = async () => {
    setGoogleStatus("redirecting");
    setError(null);
    const { error: err } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: REDIRECT_URL },
    });
    if (err) {
      setGoogleStatus("error");
      setError(err.message);
    }
  };

  return (
    <AuthLayout title="Đăng nhập" subtitle="Đăng nhập với Google hoặc qua email">
      {status === "sent" ? (
        <div className="text-center text-sm text-gray-600">
          <p className="text-3xl mb-2">📬</p>
          <p>Chúng tôi đã gửi liên kết đăng nhập tới:</p>
          <p className="font-semibold text-gray-900 mt-1">{email}</p>
          <p className="mt-3 text-xs text-gray-400">
            Hãy kiểm tra hộp thư (kể cả spam) và bấm vào liên kết để vào trang quản lý.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          <button
            type="button"
            onClick={handleGoogleSignIn}
            disabled={googleStatus === "redirecting"}
            className="w-full flex items-center justify-center gap-2 border border-gray-200 bg-white text-gray-700 font-medium py-2 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
          >
            <GoogleIcon />
            {googleStatus === "redirecting" ? "Đang chuyển hướng…" : "Đăng nhập với Google"}
          </button>

          <div className="flex items-center gap-3">
            <span className="flex-1 h-px bg-gray-200" />
            <span className="text-xs text-gray-400 uppercase tracking-wide">hoặc</span>
            <span className="flex-1 h-px bg-gray-200" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="email@example.com"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 focus:border-red-400 focus:ring focus:ring-red-100 outline-none"
            />
            {error && <p className="text-sm text-rose-700">{error}</p>}
            <button
              type="submit"
              disabled={status === "sending"}
              className="w-full bg-red-700 text-white font-medium py-2 rounded-lg hover:bg-red-800 disabled:opacity-50 transition-colors"
            >
              {status === "sending" ? "Đang gửi…" : "Gửi liên kết đăng nhập"}
            </button>
            <p className="text-xs text-gray-500 text-center">
              Chưa có tài khoản?{" "}
              <Link to="/signup" className="text-red-700 font-medium hover:underline">
                Đăng ký dạy
              </Link>
            </p>
          </form>
        </div>
      )}
    </AuthLayout>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"
      />
      <path
        fill="#FBBC05"
        d="M3.964 10.706A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.997 8.997 0 0 0 0 9c0 1.452.348 2.827.957 4.038l3.007-2.332z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.962L3.964 7.294C4.672 5.167 6.656 3.58 9 3.58z"
      />
    </svg>
  );
}

interface AuthLayoutProps {
  title: string;
  subtitle: string;
  children: React.ReactNode;
}

export function AuthLayout({ title, subtitle, children }: AuthLayoutProps) {
  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="brand-card p-8">
          <div className="text-center mb-6">
            <Link
              to="/"
              className="inline-flex items-center gap-2 font-display font-extrabold text-2xl text-red-700"
            >
              Việt <span className="text-red-300">Parenting</span>
            </Link>
            <h1 className="font-display text-2xl font-extrabold mt-4 text-red-700">{title}</h1>
            <p className="text-sm text-gray-500 mt-1">{subtitle}</p>
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}
